import type { BroadcastModalProps } from "@healthos/client-data";
import { Button, ModalDescription, ModalTitle, PulseOrb } from "@healthos/ui";
import { ArrowSquareOut, Article, PlayCircle } from "@phosphor-icons/react";
import type { ReactNode } from "react";

/** A Heartbeat broadcast as the member sees it. The admin preview renders this same card. */
export function BroadcastCard({
  broadcast,
  onPrimary,
  onDismiss,
  preview = false,
}: {
  broadcast: BroadcastModalProps;
  onPrimary?: () => void;
  onDismiss?: () => void;
  /** Outside a dialog, so the title and body are plain elements. */
  preview?: boolean;
}) {
  const Title = preview ? PlainTitle : DialogTitle;
  const Body = preview ? PlainBody : DialogBody;
  return (
    <div className="flex flex-col items-center px-6 pb-8 pt-8 text-center sm:px-8">
      <div className="flex items-center gap-3">
        <PulseOrb size="inline" />
        <span className="font-sans text-xs font-medium text-ink-eyebrow">{broadcast.eyebrow}</span>
      </div>
      <Title>{broadcast.title || "Your title"}</Title>
      <Body>{broadcast.body || "What you want members to know."}</Body>
      {broadcast.contentTitle ? (
        <div className="mt-5 flex w-full items-center gap-3 rounded-xl border border-border-subtle bg-surface-ambient px-4 py-3 text-left">
          {broadcast.type === "reset" ? (
            <PlayCircle
              size={22}
              weight="duotone"
              className="shrink-0 text-ink-brand"
              aria-hidden
            />
          ) : (
            <Article size={22} weight="duotone" className="shrink-0 text-ink-brand" aria-hidden />
          )}
          <span className="min-w-0 truncate text-sm font-medium text-ink-heading">
            {broadcast.contentTitle}
          </span>
        </div>
      ) : null}
      <div className="mt-6 flex w-full flex-col gap-2">
        <Button onClick={onPrimary} className="w-full">
          <span className="inline-flex items-center gap-2">
            {broadcast.cta}
            {broadcast.external ? <ArrowSquareOut size={16} aria-hidden /> : null}
          </span>
        </Button>
        {broadcast.linkHost ? (
          <p className="text-sm text-ink-secondary">Opens {broadcast.linkHost} in a new tab</p>
        ) : null}
        {broadcast.target ? (
          <Button variant="ghost" onClick={onDismiss} className="w-full">
            Not now
          </Button>
        ) : null}
      </div>
    </div>
  );
}

const TITLE_CLASS =
  "mt-3 max-w-[24ch] text-balance font-title text-2xl font-medium leading-[1.12] tracking-[-0.02em] text-ink-primary";
const BODY_CLASS =
  "mt-3 max-w-[40ch] whitespace-pre-line text-base leading-relaxed text-ink-secondary";

function DialogTitle({ children }: { children: ReactNode }) {
  return (
    <ModalTitle asChild>
      <h2 className={TITLE_CLASS}>{children}</h2>
    </ModalTitle>
  );
}

function PlainTitle({ children }: { children: ReactNode }) {
  return <h3 className={TITLE_CLASS}>{children}</h3>;
}

function DialogBody({ children }: { children: ReactNode }) {
  return (
    <ModalDescription asChild>
      <p className={BODY_CLASS}>{children}</p>
    </ModalDescription>
  );
}

function PlainBody({ children }: { children: ReactNode }) {
  return <p className={BODY_CLASS}>{children}</p>;
}
